// /dashboard/risk-assessment/test entry point.
//
// Owns the full lifecycle of the questionnaire experience:
//
//   1. Boot:         GET /assessment/questionnaires (form definitions)
//                  + GET /assessment/results        (existing draft / latest profile)
//   2. Answering:    Mounts <AssessmentWizard /> backed by useAssessmentForm.
//                    Auto-save hits POST /save-progress every 5 seconds of
//                    inactivity.
//   3. Calculate:    Flushes the auto-save, POSTs /calculate-full-profile,
//                    then POSTs /save-results so the SPA's "latest profile"
//                    is in lock-step with what we just rendered.
//   4. Results:      Replaces the wizard with a results dashboard
//                    (RiskRadarChart + AllocationDonut + score pills).
//
// The component intentionally drives the step machine via local state
// rather than route segments so a transient calculation failure can be
// retried without losing the user's place.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import {
    extractMissingIds,
    riskApi,
    type CalculateProfileResponse,
    type PersistedAssessmentResult,
    type QuestionnairesResponse,
    type RawAnswers,
} from './api/riskApi';
import { AssessmentWizard } from './components/AssessmentWizard';
import { RiskResultsDashboard } from './components/RiskResultsDashboard';
import { useAssessmentForm } from './hooks/useAssessmentForm';

// ---------------------------------------------------------------------------
// Boot state -- one lifecycle ref so we never end up with half-loaded UI.
// ---------------------------------------------------------------------------

type BootState =
    | { kind: 'loading' }
    | {
          kind: 'ready';
          questionnaires: QuestionnairesResponse;
          persisted: PersistedAssessmentResult | null;
      }
    | { kind: 'error'; message: string };

// View modes inside the flow. We keep them local (not URL-driven) so
// the user can flip between the wizard and the results without the
// browser losing the "you've just calculated" state on a refresh.
type ViewMode = 'wizard' | 'calculating' | 'results';

interface CalculationError {
    /** Plain message for the surface-card-error block. */
    message: string;
    /** When the failure was a 422, the IDs the wizard should highlight. */
    invalidIds: string[];
}

// ---------------------------------------------------------------------------
// Calculating skeleton -- gives the user something to watch while
// /calculate-full-profile + /save-results round-trip.
// ---------------------------------------------------------------------------

const CalculatingPanel = () => (
    <div className="surface-card-skeleton" aria-live="polite">
        <h2 className="section-title mb-2">
            Processing institutional analysis…
        </h2>
        <p className="muted text-sm mb-6">
            Scoring 43 answers against the Forge Ultra capital-markets model.
        </p>
        <div className="space-y-3">
            <div className="skeleton-line w-full" />
            <div className="skeleton-line w-5/6" />
            <div className="skeleton-line w-2/3" />
            <div className="skeleton-line-lg w-1/3 mt-6" />
            <div className="skeleton-line w-full" />
            <div className="skeleton-line w-4/5" />
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Main flow component
//
// The Results dashboard itself lives in
// ``components/RiskResultsDashboard.tsx``; this component owns the
// state machine (boot → wizard → calculating → results), wires up
// auto-save, and forwards the calculated profile + raw answers to
// the dashboard so the "Generate PDF Report" button can re-submit
// the same payload to /assessment/report/download.
// ---------------------------------------------------------------------------

export const RiskAssessmentFlow = () => {
    const [searchParams] = useSearchParams();
    const retakeRequested = searchParams.get('retake') === '1';
    const viewParam = searchParams.get('view'); // "results" -> open straight to results

    const [boot, setBoot] = useState<BootState>({ kind: 'loading' });
    const [view, setView] = useState<ViewMode>('wizard');
    const [profile, setProfile] = useState<CalculateProfileResponse | null>(
        null,
    );
    // Verbatim raw answers that produced ``profile`` -- forwarded to
    // /report/download so the PDF is built from the same inputs the
    // dashboard rendered. Sourced either from the live wizard payload
    // (fresh calculation) or from the persisted ``raw_answers`` column
    // (when a returning user lands directly on results).
    const [profileAnswers, setProfileAnswers] = useState<RawAnswers | null>(
        null,
    );
    const [calcError, setCalcError] = useState<CalculationError | null>(null);

    // Boot: fetch questionnaires + any persisted draft / completed
    // profile in parallel. We treat the questionnaires call as the
    // critical path -- without it we cannot render. /results is
    // optional (404 = brand-new user).
    useEffect(() => {
        let cancelled = false;
        Promise.all([riskApi.getQuestionnaires(), riskApi.getLatestResults()])
            .then(([questionnaires, persisted]) => {
                if (cancelled) return;
                setBoot({ kind: 'ready', questionnaires, persisted });
                // If the user has a completed profile and isn't here to
                // retake, drop them straight into the results panel.
                // ``view=results`` deep-link wins over ``retake=1``.
                if (
                    persisted &&
                    persisted.risk_scores &&
                    persisted.risk_profile &&
                    persisted.portfolio &&
                    persisted.persona &&
                    !retakeRequested
                ) {
                    setProfile({
                        // Synthesise a CalculateProfileResponse from the
                        // persisted row so the results panel can render
                        // without a fresh network call.
                        user: '', // unknown at this point; the panel falls back gracefully
                        risk_scores: persisted.risk_scores,
                        risk_profile: persisted.risk_profile,
                        portfolio: persisted.portfolio,
                        persona: persisted.persona as Record<string, unknown>,
                        // ``style_score`` and ``insights`` are now persisted
                        // alongside the rest of the profile (backend
                        // migration 0005_style_insights). Rows that pre-date
                        // the migration come back as NULL -- in that case we
                        // fall back to a clearly-labelled empty state with
                        // a hint to retake so the dashboard does not pretend
                        // to know the user's behavioral scores.
                        style_score: persisted.style_score ?? 0,
                        insights:
                            persisted.insights ?? {
                                emotional_psychological: {
                                    score: 0,
                                    average: 0,
                                    level: 'moderate',
                                    description:
                                        'Retake the assessment to refresh your behavioral insights.',
                                    insight: '',
                                    question_ids: [],
                                },
                                behavioral: {
                                    score: 0,
                                    average: 0,
                                    level: 'moderate',
                                    description:
                                        'Retake the assessment to refresh your behavioral insights.',
                                    insight: '',
                                    question_ids: [],
                                },
                            },
                    });
                    // Forward the persisted ``raw_answers`` so the
                    // "Generate PDF Report" CTA can re-submit them
                    // verbatim. Older rows may have ``raw_answers``
                    // null -- the dashboard handles that case by
                    // disabling the button with an explanatory hint.
                    setProfileAnswers(persisted.raw_answers ?? null);
                    if (viewParam === 'results') setView('results');
                }
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('[RiskAssessmentFlow] boot failed', err);
                setBoot({
                    kind: 'error',
                    message:
                        'We could not load the questionnaire. Refresh the page or come back in a moment.',
                });
            });
        return () => {
            cancelled = true;
        };
    }, [retakeRequested, viewParam]);

    // The hook needs the persisted draft to hydrate. Until boot
    // completes we pass null + ``enabled=false`` so an empty-state
    // auto-save does NOT race with the hydration.
    const persistedDraft =
        boot.kind === 'ready' ? boot.persisted?.raw_answers ?? null : null;
    const formEnabled = boot.kind === 'ready' && view === 'wizard';
    const { answers, setAnswer, saveStatus, flushSave } = useAssessmentForm(
        persistedDraft,
        formEnabled,
    );

    const handleCalculate = async () => {
        setCalcError(null);
        setView('calculating');
        try {
            // Best-effort flush of the auto-save so the backend has the
            // latest draft on file before scoring. Failures here are
            // non-fatal -- the calculation still proceeds.
            await flushSave().catch(() => undefined);

            const fullPayload: RawAnswers = answers;
            const result = await riskApi.calculateProfile(fullPayload);
            setProfile(result);
            setProfileAnswers(fullPayload);

            // Persist results so /results stays in sync with what the
            // user is currently looking at. Forward raw_answers so the
            // form re-hydrates correctly on the next visit.
            await riskApi
                .saveFullResults({
                    risk_scores: result.risk_scores,
                    risk_profile: result.risk_profile,
                    portfolio: result.portfolio,
                    persona: result.persona,
                    raw_answers: fullPayload,
                    // Forward the two computed extras so the persisted
                    // row mirrors the /calculate-full-profile response
                    // 1:1. Without these the next "View Latest Results"
                    // visit would render a zero Style pill and an empty
                    // radar chart (the bug fixed by 0005_style_insights).
                    style_score: result.style_score,
                    insights: result.insights,
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.warn(
                        '[RiskAssessmentFlow] /save-results failed; results still rendered locally.',
                        err,
                    );
                });

            setView('results');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[RiskAssessmentFlow] calculate failed', err);
            const missingIds = extractMissingIds(err);
            if (missingIds && missingIds.length > 0) {
                setCalcError({
                    message: `Please answer the highlighted question${
                        missingIds.length > 1 ? 's' : ''
                    } before calculating your profile.`,
                    invalidIds: missingIds,
                });
            } else {
                setCalcError({
                    message:
                        'The Forge Ultra scoring service is temporarily unavailable. Please try again in a moment.',
                    invalidIds: [],
                });
            }
            setView('wizard');
        }
    };

    const handleRetake = () => {
        setProfile(null);
        setProfileAnswers(null);
        setCalcError(null);
        setView('wizard');
    };

    /**
     * Section-boundary auto-save -- fired by the wizard whenever the
     * user clicks "Continue" on the last question of a section. We
     * flush the debounced /save-progress so the backend always has a
     * full section's worth of answers on file before the user moves
     * on. Failures are surfaced via the in-wizard SaveBadge; we do
     * NOT block navigation on them.
     */
    const handleSectionAdvance = async () => {
        await flushSave().catch(() => undefined);
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (boot.kind === 'loading') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Risk Assessment"
                subtitle="Preparing your questionnaire…"
            >
                <div className="surface-card-skeleton">
                    <div className="skeleton-line-lg w-1/3 mb-4" />
                    <div className="skeleton-line w-full mb-2" />
                    <div className="skeleton-line w-2/3" />
                </div>
            </Layout>
        );
    }

    if (boot.kind === 'error') {
        return (
            <Layout
                sidebar={<Sidebar />}
                title="Risk Assessment"
                subtitle="Something went wrong."
            >
                <p className="surface-card-error">{boot.message}</p>
                <Link
                    to="/dashboard/risk-assessment"
                    className="mt-4 inline-block text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                    ← Back to Risk Intelligence Suite
                </Link>
            </Layout>
        );
    }

    return (
        <Layout
            sidebar={<Sidebar />}
            title={view === 'results' ? 'Your Risk Profile' : 'Risk Assessment'}
            subtitle={
                view === 'results'
                    ? 'Based on your most recent answers.'
                    : 'Forty-three questions across four dimensions. Auto-saved as you go.'
            }
        >
            {/* Persistent breadcrumb back to the suite landing page. */}
            <div className="mb-4">
                <Link
                    to="/dashboard/risk-assessment"
                    className="text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400"
                >
                    ← Risk Intelligence Suite
                </Link>
            </div>

            {calcError && (
                <p className="surface-card-error mb-6">{calcError.message}</p>
            )}

            {view === 'calculating' && <CalculatingPanel />}

            {view === 'wizard' && (
                <AssessmentWizard
                    questionnaires={boot.questionnaires}
                    answers={answers}
                    setAnswer={setAnswer}
                    onCalculate={handleCalculate}
                    invalidIds={calcError?.invalidIds}
                    saveStatus={saveStatus}
                    onSectionAdvance={handleSectionAdvance}
                />
            )}

            {view === 'results' && profile && (
                <RiskResultsDashboard
                    profile={profile}
                    rawAnswers={profileAnswers}
                    onRetake={handleRetake}
                />
            )}
        </Layout>
    );
};

export default RiskAssessmentFlow;
